import React, { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { motion, HTMLMotionProps, AnimatePresence } from 'framer-motion';
import { AlertIcon, CloseIcon, InfoIcon } from './icons';

/** Une clases truthy sin dependencias extra. */
export function cn(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

// ── Motion tokens ───────────────────────────────────────────────────────────
// Valores exactos del skill better-ui. No aproximar.

/** Feedback táctil de botón: escala al presionar. */
export const pressScale = { scale: 0.96 };
/** Spring de estado (indicadores que se deslizan). bounce siempre 0. */
export const springState = { type: 'spring' as const, duration: 0.25, bounce: 0 };
/** Transición corta para color/opacidad de alta frecuencia. */
export const quick = { duration: 0.15, ease: 'easeOut' as const };
/** Cross-fade de icono contextual. Valores exactos del skill: bounce 0. */
export const springIcon = { type: 'spring' as const, duration: 0.3, bounce: 0 };
/** Curva estándar de entrada/salida. */
export const easeStandard = [0.2, 0, 0, 1] as [number, number, number, number];

/** Entrada escalonada de página: el padre reparte, cada hijo usa `enterChild`. */
export const staggerParent = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.09 } },
};
export const enterChild = {
  hidden: { opacity: 0, y: 12, filter: 'blur(4px)' },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.4, ease: [0.2, 0, 0, 1] as [number, number, number, number] },
  },
};

// ── Card ─────────────────────────────────────────────────────────────────────

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Levanta el shadow al pasar el mouse. Solo para tarjetas accionables. */
  interactive?: boolean;
  as?: 'div' | 'section';
}

/**
 * Superficie base: fondo blanco, radio 2xl y profundidad por shadow en capas
 * (nunca por borde). El padding lo pone el consumidor vía className.
 */
export const Card: React.FC<CardProps> = ({
  interactive = false,
  as = 'div',
  className,
  children,
  ...rest
}) => {
  const Comp = as;
  return (
    <Comp
      className={cn(
        'bg-white rounded-2xl shadow-elevate',
        interactive && 'transition-shadow duration-150 ease-out hover:shadow-elevate-hover',
        className,
      )}
      {...rest}
    >
      {children}
    </Comp>
  );
};

// ── Button ────────────────────────────────────────────────────────────────────

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

interface ButtonProps extends HTMLMotionProps<'button'> {
  variant?: ButtonVariant;
  /** Desactiva el scale al presionar donde el movimiento distrae. */
  static?: boolean;
  /** Icono principal, antes del texto. Ajusta el padding para que quede óptico. */
  icon?: React.ReactNode;
  /** Botón cuadrado sin texto (cerrar, expandir). Necesita aria-label. */
  iconOnly?: boolean;
}

const buttonBase =
  'inline-flex items-center justify-center gap-2 rounded-lg py-2 text-sm font-semibold ' +
  'transition-colors duration-150 ease-out outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-gappex-primary/40 disabled:opacity-50 disabled:pointer-events-none';

const buttonVariants: Record<ButtonVariant, string> = {
  primary: 'bg-gappex-primary text-white hover:bg-blue-700 shadow-elevate',
  secondary: 'bg-white text-slate-600 shadow-elevate hover:text-slate-900 hover:shadow-elevate-hover',
  ghost: 'bg-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-100',
  danger: 'bg-white text-red-600 shadow-elevate hover:bg-red-50 hover:shadow-elevate-hover',
};

/**
 * Botón con feedback de presión interrumpible (scale 0.96).
 *
 * El padding es óptico: con icono adelante, ese lado lleva 2px menos que el
 * lado del texto, si no el icono parece empujado contra el borde.
 */
export const Button: React.FC<ButtonProps> = ({
  variant = 'secondary',
  static: isStatic = false,
  icon,
  iconOnly = false,
  className,
  children,
  ...rest
}) => (
  <motion.button
    whileTap={isStatic ? undefined : pressScale}
    transition={quick}
    className={cn(
      buttonBase,
      iconOnly ? 'h-9 w-9 p-0' : icon ? 'ps-3 pe-3.5' : 'px-3.5',
      buttonVariants[variant],
      className,
    )}
    {...rest}
  >
    <>
      {icon}
      {children}
    </>
  </motion.button>
);

// ── Segmented control ─────────────────────────────────────────────────────────

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  title?: string;
}

interface SegmentedProps<T extends string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  ariaLabel?: string;
}

/**
 * Selector de dos o más opciones con indicador que se desliza. El `layoutId`
 * es único por instancia (useId) para que cada control mueva SU pastilla y no
 * una compartida entre filas.
 */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  disabled = false,
  ariaLabel,
}: SegmentedProps<T>) {
  const layoutId = useId();
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn(
        'relative inline-flex rounded-lg bg-slate-100 p-0.5',
        disabled && 'opacity-50 pointer-events-none',
      )}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            title={opt.title}
            aria-pressed={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              'relative z-10 rounded-md px-2.5 py-1 text-xs font-semibold transition-colors duration-150',
              active ? 'text-white' : 'text-slate-500 hover:text-slate-700',
            )}
          >
            {active && (
              <motion.span
                layoutId={layoutId}
                transition={springState}
                className="absolute inset-0 -z-10 rounded-md bg-gappex-primary shadow-elevate"
              />
            )}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ── StatCard (KPI) ─────────────────────────────────────────────────────────────

export type Tone = 'default' | 'info' | 'positive' | 'warning' | 'negative';

const toneDot: Record<Tone, string> = {
  default: 'bg-gappex-primary',
  info: 'bg-cyan-500',
  positive: 'bg-emerald-500',
  warning: 'bg-amber-500',
  negative: 'bg-red-500',
};

const toneValue: Record<Tone, string> = {
  default: 'text-gappex-primary',
  info: 'text-cyan-600',
  positive: 'text-emerald-600',
  warning: 'text-amber-600',
  negative: 'text-red-500',
};

interface StatCardProps {
  label: string;
  value: string;
  sub?: React.ReactNode;
  tone?: Tone;
  /** Explicación al pasar el mouse, para métricas que necesitan letra chica. */
  title?: string;
}

/** Tarjeta de KPI: punto de color + label en caps, número grande, sublínea. */
export const StatCard: React.FC<StatCardProps> = ({ label, value, sub, tone = 'default', title }) => (
  <Card className="p-5" title={title}>
    <div className="flex items-center gap-2">
      <span className={cn('h-1.5 w-1.5 rounded-full', toneDot[tone])} />
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
    </div>
    <p className={cn('mt-2.5 text-2xl font-bold tabular-nums', toneValue[tone])}>{value}</p>
    {sub && <div className="mt-1 text-xs text-slate-400">{sub}</div>}
  </Card>
);

// ── Chip de valor calculado ─────────────────────────────────────────────────

interface CalcChipProps {
  children: React.ReactNode;
  /** Resalta el valor derivado principal (Digital / Gappex). */
  accent?: boolean;
  title?: string;
}

/** Muestra un número calculado, no editable. Fondo tenue para distinguirlo. */
export const CalcChip: React.FC<CalcChipProps> = ({ children, accent = false, title }) => (
  <span
    title={title}
    className={cn(
      'inline-block rounded-md bg-slate-100 px-2 py-1 text-sm font-semibold tabular-nums',
      accent ? 'text-gappex-primary' : 'text-slate-600',
    )}
  >
    {children}
  </span>
);

// ── Badge que entra y sale con animación ───────────────────────────────────────

interface AnimatedBadgeProps {
  show: boolean;
  children: React.ReactNode;
  title?: string;
  className?: string;
}

/** Etiqueta contextual (ej. "sin costo") que aparece con scale+blur. */
export const AnimatedBadge: React.FC<AnimatedBadgeProps> = ({ show, children, title, className }) => (
  <AnimatePresence initial={false}>
    {show && (
      <motion.span
        title={title}
        initial={{ opacity: 0, scale: 0.25, filter: 'blur(4px)' }}
        animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
        exit={{ opacity: 0, scale: 0.25, filter: 'blur(4px)' }}
        transition={springState}
        className={cn(
          'inline-block text-[10px] font-bold uppercase tracking-wide rounded px-1.5 py-0.5',
          className,
        )}
      >
        {children}
      </motion.span>
    )}
  </AnimatePresence>
);

// ── Panel (sección con encabezado) ───────────────────────────────────────────

interface PanelProps {
  title: React.ReactNode;
  /** Letra chica bajo el título: qué mide la sección y con qué recorte. */
  subtitle?: React.ReactNode;
  /** Controles alineados a la derecha del encabezado. */
  actions?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

/**
 * Card con el encabezado estandarizado de las secciones del dashboard. Existe
 * para que título, subtítulo y acciones tengan el mismo ritmo en todas: si el
 * encabezado se escribe a mano en cada componente, se desalinean solos.
 */
export const Panel: React.FC<PanelProps> = ({ title, subtitle, actions, className, children }) => (
  <Card as="section" className={cn('p-6', className)} aria-label={typeof title === 'string' ? title : undefined}>
    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <h2 className="text-base font-semibold text-gappex-secondary">{title}</h2>
        {subtitle && <p className="mt-1 text-xs text-slate-400">{subtitle}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
    {children}
  </Card>
);

// ── Badge ────────────────────────────────────────────────────────────────────

export type BadgeTone = 'neutral' | 'info' | 'positive' | 'warning' | 'negative';

const badgeTones: Record<BadgeTone, string> = {
  neutral: 'bg-slate-100 text-slate-600',
  info: 'bg-blue-50 text-blue-700',
  positive: 'bg-emerald-50 text-emerald-700',
  warning: 'bg-amber-50 text-amber-700',
  negative: 'bg-red-50 text-red-600',
};

interface BadgeProps {
  tone?: BadgeTone;
  children: React.ReactNode;
  title?: string;
  className?: string;
}

/** Etiqueta de estado estática: el color nunca es el único indicio, el texto lo dice. */
export const Badge: React.FC<BadgeProps> = ({ tone = 'neutral', children, title, className }) => (
  <span
    title={title}
    className={cn(
      'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap',
      badgeTones[tone],
      className,
    )}
  >
    {children}
  </span>
);

// ── Alert ────────────────────────────────────────────────────────────────────

export type AlertTone = 'info' | 'warning' | 'error';

const alertTones: Record<AlertTone, { surface: string; icon: string; title: string; body: string }> = {
  info: { surface: 'bg-sky-50 border-sky-500', icon: 'text-sky-500', title: 'text-sky-900', body: 'text-sky-800' },
  warning: { surface: 'bg-amber-50 border-amber-500', icon: 'text-amber-500', title: 'text-amber-900', body: 'text-amber-800' },
  error: { surface: 'bg-red-50 border-red-500', icon: 'text-red-500', title: 'text-red-900', body: 'text-red-700' },
};

interface AlertProps {
  tone?: AlertTone;
  title?: React.ReactNode;
  children?: React.ReactNode;
  /** Acción de recuperación (reintentar, deshacer). */
  action?: React.ReactNode;
  onClose?: () => void;
  /** Espaciado del contexto. Va acá y no en un wrapper: AnimatePresence solo
   *  anima la salida de su hijo directo. */
  className?: string;
}

/**
 * Aviso a nivel página. Entra suave y sale más corto que como entró: el foco
 * del usuario ya se fue a otra parte. Va dentro de un <AnimatePresence>.
 */
export const Alert: React.FC<AlertProps> = ({
  tone = 'info',
  title,
  children,
  action,
  onClose,
  className,
}) => {
  const t = alertTones[tone];
  return (
    <motion.div
      role={tone === 'error' ? 'alert' : 'status'}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0, transition: { duration: 0.3, ease: easeStandard } }}
      exit={{ opacity: 0, y: -8, transition: { duration: 0.15, ease: 'easeOut' } }}
      className={cn('flex items-start gap-3 rounded-xl border-l-4 p-4 shadow-elevate', t.surface, className)}
    >
      <span className={cn('mt-px shrink-0', t.icon)}>
        {tone === 'info' ? <InfoIcon strokeWidth={2} /> : <AlertIcon strokeWidth={2} />}
      </span>
      <div className="min-w-0 flex-1">
        {title && <p className={cn('text-sm font-semibold', t.title)}>{title}</p>}
        {children && <div className={cn('text-xs', t.body, Boolean(title) && 'mt-1')}>{children}</div>}
        {action && <div className="mt-2.5">{action}</div>}
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar aviso"
          className={cn(
            '-m-1 shrink-0 rounded p-1 opacity-60 transition-opacity duration-150 ease-out hover:opacity-100',
            t.icon,
          )}
        >
          <CloseIcon strokeWidth={2} />
        </button>
      )}
    </motion.div>
  );
};

// ── Cross-fade de icono contextual ───────────────────────────────────────────

interface IconSwapProps {
  /** Cambiar esta clave dispara el cross-fade. */
  swapKey: string;
  children: React.ReactNode;
  className?: string;
}

/**
 * Intercambia un icono por otro con scale + opacity + blur en vez de esconder
 * y mostrar. `initial={false}` evita que el icono entre animado en el primer
 * render: solo se anima cuando el estado cambia de verdad.
 */
export const IconSwap: React.FC<IconSwapProps> = ({ swapKey, children, className }) => (
  <AnimatePresence mode="popLayout" initial={false}>
    <motion.span
      key={swapKey}
      initial={{ opacity: 0, scale: 0.25, filter: 'blur(4px)' }}
      animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
      exit={{ opacity: 0, scale: 0.25, filter: 'blur(4px)' }}
      transition={springIcon}
      className={cn('inline-flex', className)}
    >
      {children}
    </motion.span>
  </AnimatePresence>
);

// ── Barra de proporción ──────────────────────────────────────────────────────

interface ShareBarProps {
  /** 0 a 100. */
  pct: number;
  color: string;
  className?: string;
}

/** Riel fino que hace comparable una columna de porcentajes de un vistazo. */
export const ShareBar: React.FC<ShareBarProps> = ({ pct, color, className }) => (
  <span className={cn('block h-1.5 w-full overflow-hidden rounded-full bg-slate-100', className)}>
    <span
      className="block h-full rounded-full transition-[width] duration-300 ease-out"
      style={{ width: `${Math.max(0, Math.min(100, pct))}%`, backgroundColor: color }}
    />
  </span>
);

// ── Modal ────────────────────────────────────────────────────────────────────

interface ModalProps {
  titulo: string;
  onClose: () => void;
  children: React.ReactNode;
  /** Ancho del panel: `md` para formularios cortos, `lg` para listas. */
  size?: 'md' | 'lg';
}

/**
 * Diálogo centrado sobre un velo oscuro.
 *
 * Va por `createPortal` a `document.body` a propósito: si se renderiza en su
 * lugar del árbol, cualquier ancestro con `transform` o `filter` —por ejemplo
 * el `filter: blur()` de `enterChild`— pasa a ser el bloque contenedor del
 * `position: fixed` y el modal termina recortado adentro de la tarjeta en vez
 * de cubrir la pantalla.
 */
export const Modal: React.FC<ModalProps> = ({ titulo, onClose, children, size = 'md' }) => {
  const titleId = useId();

  // Escape cierra; el fondo no scrollea mientras el modal está abierto.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflowPrevio;
    };
  }, [onClose]);

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={quick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.98, y: 8 }}
        transition={springState}
        className={cn(
          'w-full max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-elevate-lg',
          size === 'lg' ? 'max-w-lg' : 'max-w-md',
        )}
      >
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <h3 id={titleId} className="text-lg font-semibold text-gappex-secondary">
              {titulo}
            </h3>
            <Button
              variant="ghost"
              iconOnly
              aria-label="Cerrar"
              onClick={onClose}
              className="-me-2 -mt-1.5 shrink-0"
            >
              <CloseIcon className="h-5 w-5" strokeWidth={2} />
            </Button>
          </div>
          {children}
        </div>
      </motion.div>
    </motion.div>,
    document.body,
  );
};
